"use client";

// DocsInlineSearch — the docs search folded INTO the breadcrumb, exactly the
// dashboard's OsSearch grammar: a "/ search docs" affordance you type into right
// there, with matches dropping into an overlay anchored under it, live as you
// type. Reuses the same chrome classes (.os-crumb-search / .os-search-results /
// .os-search-hit / .os-find-mark) so it is visually identical to the app search.
// The index is the docs registry (searchDocs) — pure client, always current.
//
// On a phone (collapsible) it shrinks to a magnifying-glass that expands on tap,
// so a long breadcrumb keeps its room — same as OsSearch.

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { searchDocs, type SearchHit } from "../../lib/docs/search";

// bold the matched run inside a snippet/name so the eye lands on it.
function mark(text: string, q: string): React.ReactNode {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="os-find-mark">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

export default function DocsInlineSearch({ collapsible = false }: { collapsible?: boolean }) {
  const router = useRouter();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false); // mobile: the field is expanded
  const wrapRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const term = q.trim();
    setHits(term.length < 2 ? null : searchDocs(term));
  }, [q]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) { setFocused(false); setOpen(false); }
    };
    // ⌘K / Ctrl+K quietly focuses the inline field (no visible badge — matches the
    // dashboard's clean crumb search).
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true); setFocused(true);
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("keydown", onKey); };
  }, []);

  const show = focused && q.trim().length >= 2 && hits !== null;
  const placeholder = "search docs";
  const expanded = !collapsible || open || focused || q.length > 0;

  return (
    <span
      ref={wrapRef}
      data-noswipe
      style={{ display: "inline-flex", alignItems: "baseline", gap: "0.5rem", position: "relative", minWidth: 0 }}
    >
      <span aria-hidden style={{ color: "var(--ink-4)" }}>/</span>
      {collapsible && !expanded ? (
        <button
          type="button"
          className="os-crumb-search-btn"
          aria-label={placeholder}
          data-hint-key="nav.search"
          onClick={() => { setOpen(true); setFocused(true); requestAnimationFrame(() => inputRef.current?.focus()); }}
        >
          <svg width="26" height="26" viewBox="0 0 16 16" fill="none" aria-hidden>
            <circle cx="7" cy="7" r="4.4" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10.4 10.4 14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      ) : (
        <input
          ref={inputRef}
          className="os-crumb-search"
          placeholder={placeholder}
          value={q}
          size={Math.max(placeholder.length, q.length + 1)}
          data-hint-key="nav.search"
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") { setQ(""); (e.target as HTMLInputElement).blur(); setOpen(false); }
            e.stopPropagation();
          }}
          aria-label="search the docs"
        />
      )}
      {show && (
        <div className="os-search-results" style={{ left: 0, right: "auto", width: "min(34rem, 80vw)", top: "calc(100% + 0.7rem)" }}>
          <div style={{ fontSize: "0.72rem", color: "var(--ink-4)", padding: "0.1rem 0.2rem 0.5rem" }}>in the docs · live</div>
          {hits!.length === 0 && <div style={{ color: "var(--ink-4)", fontSize: "0.85rem", padding: "0.4rem" }}>nothing matches yet.</div>}
          {hits!.map((h, i) => (
            <button
              key={`${h.href}-${i}`}
              className="os-search-hit"
              onClick={() => { router.push(h.href); setFocused(false); setOpen(false); setQ(""); }}
            >
              <span style={{ color: "var(--accent-ink)", width: "1.4rem", flexShrink: 0 }}>❋</span>
              <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <span style={{ color: "var(--ink)", fontSize: "0.95rem" }}>
                  {mark(h.heading ?? h.page, q.trim())} <span style={{ color: "var(--ink-4)", fontSize: "0.78rem" }}>· {h.page}</span>
                </span>
                {h.text && (
                  <span style={{ color: "var(--ink-3)", fontSize: "0.8rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {mark(h.text, q.trim())}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
